import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TText,
  TListOfElement,
  TGeneric,
  unionOf,
  a,
} from '@packages/systems/wfdl-ast';
import {qualify, iconName} from '@packages/systems/wfdl-Qualified';
import {type DynamoEnvValueType} from '../types';
import {TOption} from '@packages/systems/plugins/Option';
import {TDynamoOption} from '@packages/systems/plugins-utils/DynamoGateway';
import {
  toOperandExpr,
  TOperand,
} from '@packages/systems/plugins/ConditionsOperand';
import {
  FALSE,
  toBooleanTag,
  TPredicate,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import {TOptionAnyOfConditionValue} from '../dataTypes/OptionAnyOfConditionValue';

export const OptionType = TypeImplementation.Custom({
  params: ['optionUnion'],
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Text'))
  ),
});

export const dynamoOptionToText: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: TGeneric({
      arg: 'a',
      body: functionOf(
        withSystem({isBindable: true}, TOption(TDynamoOption(unionOf(a)))),
        TText
      ),
    }),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(
    opt: DynamoEnvValueType<{
      name: string;
    }>
  ) {
    if (opt === null || opt.tag === 'None') {
      return '';
    }
    return opt.val.name;
  },
};

export const dynamoOptionToListOfElements: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: TGeneric({
      arg: 'a',
      body: functionOf(
        withSystem({isBindable: true}, TOption(TDynamoOption(unionOf(a)))),
        TListOfElement
      ),
    }),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(
    opt: DynamoEnvValueType<{
      name: string;
    }>
  ) {
    if (opt === null || opt.tag === 'None') {
      return [];
    }
    return [
      {
        text: true,
        type: null,
        id: 'temp',
        children: [],
        data: {value: opt.val.name},
      },
    ];
  },
};

export const dynamoOptionCondition: EnvTypes.SafeTypedValue = {
  envType: {
    dataType: TGeneric({
      arg: 'a',
      body: functionOf(
        TOption(TDynamoOption(unionOf(a))),
        TOperand(TOption(TDynamoOption(unionOf(a))))
      ),
    }),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: toOperandExpr,
};

export const dynamoOptionIsAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TOptionAnyOfConditionValue(unionOf(a)),
          TPredicate(TOption(TDynamoOption(unionOf(a))))
        ),
      })
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (opt: DynamoEnvValueType<{id: string}>) => {
    if (opt === null || opt.tag === 'None') {
      return FALSE;
    }
    return toBooleanTag(predArgList.includes(opt.val.id));
  },
};

export const dynamoOptionIsNotAny: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TOptionAnyOfConditionValue(unionOf(a)),
          TPredicate(TOption(TDynamoOption(unionOf(a))))
        ),
      })
    ),
    exported: 'visible',
  },
  value: (predArgList: string[]) => (opt: DynamoEnvValueType<{id: string}>) => {
    if (opt === null || opt.tag === 'None') {
      return TRUE;
    }
    return toBooleanTag(!predArgList.includes(opt.val.id));
  },
};

export const dynamoOptionIsSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is set'},
      TGeneric({
        arg: 'a',
        body: TPredicate(TOption(TDynamoOption(unionOf(a)))),
      })
    ),
    exported: 'visible',
  },
  value: (opt: DynamoEnvValueType<unknown>) => {
    if (opt === null || opt.tag === 'None') {
      return FALSE;
    }
    return TRUE;
  },
};

export const dynamoOptionIsNotSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not set'},
      TGeneric({
        arg: 'a',
        body: TPredicate(TOption(TDynamoOption(unionOf(a)))),
      })
    ),
    exported: 'visible',
  },
  value: (opt: DynamoEnvValueType<unknown>) => {
    if (opt === null || opt.tag === 'None') {
      return TRUE;
    }
    return FALSE;
  },
};
